import { HttpParams } from '@angular/common/http';

/**
 * Builds HttpParams from a query object.
 * - Skips nullish/empty values
 * - Serializes arrays as comma-separated lists
 * - Serializes nested objects using bracket notation
 * - Special-cases common API patterns: `filter` and `sort`
 */
export function buildHttpParams(
  queryParams?: Record<string, unknown>
): HttpParams {
  let params = new HttpParams();

  if (!queryParams) {
    return params;
  }

  const appendParam = (key: string, value: unknown): void => {
    if (!isValidParam(value)) {
      return;
    }

    if (Array.isArray(value)) {
      const serialized = value
        .filter((v) => isValidParam(v))
        .map((v) => serializeScalar(v))
        .join(',');

      if (isValidParam(serialized)) {
        params = params.set(key, serialized);
      }

      return;
    }

    if (value instanceof Date) {
      params = params.set(key, value.toISOString());

      return;
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;

      if (key === 'filter') {
        Object.entries(record).forEach(([nestedKey, nestedValue]) => {
          if (!isValidParam(nestedValue)) {
            return;
          }

          const serialized = Array.isArray(nestedValue)
            ? nestedValue
                .filter((v) => isValidParam(v))
                .map((v) => serializeScalar(v))
                .join(',')
            : serializeScalar(nestedValue);

          params = params.set(`filter[${nestedKey}]`, serialized);

        });

        return;
      }

      if (key === 'sort' && ('field' in record || 'order' in record)) {
        const { field, order } = record as {
          field?: unknown;
          order?: unknown;
        };

        if (isValidParam(field)) {
          params = params.set('sort', serializeScalar(field));
        }

        if (isValidParam(order)) {
          params = params.set('order', serializeScalar(order));
        }

        return;
      }

      Object.entries(record).forEach(([nestedKey, nestedValue]) => {
        appendParam(`${key}[${nestedKey}]`, nestedValue);
      });

      return;
    }

    params = params.set(key, serializeScalar(value));
  };

  Object.entries(queryParams).forEach(([key, value]) => appendParam(key, value));

  return params;
}

/**
 * Checks whether a value is valid as a query parameter.
 * Filters out null, undefined, and empty-trimmed strings.
 */
export function isValidParam(value: unknown): boolean {
  return (
    value !== null &&
    value !== undefined &&
    !(typeof value === 'string' && value.trim() === '')
  );
}

/**
 * Serializes scalar-like values into strings.
 */
function serializeScalar(value: unknown): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
}
